/**
 * Coach-level drill recommendations for the v2 /drill hub coach strip.
 *
 * `coachDrillPick` selects the single highest-impact skill + mode
 * combination to surface in the coach strip, using the priority rules
 * from drill-hub-design-v2.md §4.
 *
 * Per-skill mode recommendation lives in skill-state.ts (recommendedMode).
 * This file is only the multi-skill "what should I work on today?" picker.
 */

import type { ExerciseSkillId, SkillState } from "./skill-state.js";

/**
 * Return the one skill that gets the ★ REC chip for a given mode tab in the
 * v2.1 tabbed-grid hub (drill-modes-design-v2.1.md §5.2).
 *
 * Algorithm: filter skills whose recommendedMode === mode, then pick the
 * highest-scoring one. Score = (unmastered items) × weight, where:
 *   Listen: 1.5, Drill: 1.0, Challenge: 0.7
 *
 * Returns null when no skill recommends that mode or the list is empty.
 */
export function recommendedSkillForMode(
  mode: "listen" | "practice" | "challenge",
  skills: readonly SkillState[],
): SkillState | null {
  const weight = mode === "listen" ? 1.5 : mode === "challenge" ? 0.7 : 1.0;
  const candidates = skills.filter((s) => s.recommendedMode === mode);
  if (candidates.length === 0) return null;
  return candidates.reduce((best, s) => {
    const score = (s.exercise.total - s.exercise.mastered) * weight;
    const bestScore = (best.exercise.total - best.exercise.mastered) * weight;
    return score > bestScore ? s : best;
  });
}

export interface CoachDrillRecommendation {
  skillId: ExerciseSkillId;
  mode: "listen" | "practice" | "challenge";
  /** One-sentence reason for display in the coach strip. */
  reason: string;
  /** Estimated session length in minutes. */
  estimatedMinutes: number;
}

const SKILL_LABELS: Record<ExerciseSkillId, string> = {
  scales: "Scale degree drill",
  intervals: "Interval drill",
  chords: "Chord quality drill",
  inversions: "Chord inversion drill",
  cadences: "Cadence drill",
  modes: "Mode drill",
  progressions: "Progression drill",
  dictation: "Melodic dictation",
  "rhythmic-dictation": "Rhythmic dictation",
  "fret-intervals": "Fretboard intervals & octaves",
  "fret-scale-degrees": "Scale degrees on the neck",
  "fret-triads": "Fretboard triad shapes",
  "fret-inversions": "Fretboard triad inversions",
  "fret-caged": "CAGED shapes",
  "fret-caged-anchoring": "CAGED anchoring",
  "fret-scale-degrees-caged": "Scale degrees in CAGED shapes",
  "fret-diatonic": "Diatonic triads on the neck",
  "fret-diatonic-minor": "Minor key diatonic triads on the neck",
  "fret-seventh": "7th chord shapes on the neck",
};

const LISTEN_MINS = 4;
const DRILL_MINS = 7;
const CHALLENGE_MINS = 5;

/**
 * Pick the single highest-impact skill + mode for the coach strip.
 *
 * Selection priority (drill-hub spec §4):
 *  1. Any skill recommended for Listen AND listen.count < 5 (newcomer).
 *  2. Any skill with 7-day lapse rate ≥ 30% (faltering — prioritise Drill).
 *  3. Any skill whose Challenge just unlocked (state="open", attempts=0).
 *  4. Fallback: skill with the largest unmastered pool weighted by lapse rate.
 *
 * Returns null if skills is empty (caller renders the placement CTA instead).
 */
export function coachDrillPick(
  skills: readonly SkillState[],
): CoachDrillRecommendation | null {
  if (skills.length === 0) return null;

  // Priority 1 — newcomer: send to Listen first
  const newcomer = skills.find(
    (s) => s.recommendedMode === "listen" && s.listen.count < 5,
  );
  if (newcomer) {
    const n = newcomer.listen.count;
    return {
      skillId: newcomer.id,
      mode: "listen",
      reason: `You've only heard ${SKILL_LABELS[newcomer.id]} ${n} time${n === 1 ? "" : "s"} — meet the sound before drilling.`,
      estimatedMinutes: LISTEN_MINS,
    };
  }

  // Priority 2 — lapsing: most-lapsed skill first
  const lapsing = skills
    .filter((s) => s.exercise.lapse7d >= 0.3)
    .slice()
    .sort((a, b) => b.exercise.lapse7d - a.exercise.lapse7d)[0];
  if (lapsing) {
    const pct = Math.round(lapsing.exercise.lapse7d * 100);
    return {
      skillId: lapsing.id,
      mode: "practice",
      reason: `You missed ${pct}% of ${SKILL_LABELS[lapsing.id]} items last week — extra reps will pull them back.`,
      estimatedMinutes: DRILL_MINS,
    };
  }

  // Priority 3 — fresh Challenge unlock
  const freshChallenge = skills.find(
    (s) => s.challenge.state === "open" && s.challenge.attempts === 0,
  );
  if (freshChallenge) {
    return {
      skillId: freshChallenge.id,
      mode: "challenge",
      reason: `${SKILL_LABELS[freshChallenge.id]} Challenge just unlocked — time to stress-test what you know.`,
      estimatedMinutes: CHALLENGE_MINS,
    };
  }

  // Priority 4 — most impactful Practice session
  // Score = unmastered item count × max(lapse rate, floor of 0.05 so new skills aren't ignored)
  const scored = skills
    .map((s) => ({
      skill: s,
      score: (s.exercise.total - s.exercise.mastered) * Math.max(s.exercise.lapse7d, 0.05),
    }))
    .sort((a, b) => b.score - a.score);

  const best = scored[0];
  if (!best) return null;

  return {
    skillId: best.skill.id,
    mode: "practice",
    reason: `${SKILL_LABELS[best.skill.id]} has the most room to grow right now.`,
    estimatedMinutes: DRILL_MINS,
  };
}
